import { Text } from '@mantine/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { DisplayComponentBaseView } from '../../../../../../../react-commons/views/DisplayComponentBaseView';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultProcessArtifactResource,
    ProcessArtifactMutationResourceSchema,
    ProcessArtifactPropertyValidation,
    useProcessArtifactActions,
} from '../../../../../resource/resources/artifacts/process_artifact/processArtifactResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { ResourceValidationTitleBar } from '../../../../reusable-components/validation/ResourceValidationTitleBar';
import { OverviewComponentPropertyValidationInformation } from '../../../../util/overviewUtils';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { ProcessArtifactContent } from './ProcessArtifactContent';

const ProcessArtifactOverviewContent = ({ id, displayedInOverview }: { id: string; displayedInOverview: boolean }) => {
    const readOnly = useSelector(selectReadOnly);

    const processArtifactActions = useProcessArtifactActions();
    const processArtifactQuery = processArtifactActions.useGetResource(id);
    const updateProcessArtifact = processArtifactActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: ProcessArtifactMutationResourceSchema,
        defaultValues: defaultProcessArtifactResource,
        submitHandler: async (data) => {
            await updateProcessArtifact.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: processArtifactQuery.data,
    });

    return (
        <QueryBaseComponent name="ProcessArtifact" query={processArtifactQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <ProcessArtifactContent
                    displayedInOverview={displayedInOverview}
                    form={form}
                    formNames={formNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const ProcessArtifactOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const processArtifactActions = useProcessArtifactActions();

    const parametersQuery = processArtifactActions.useGetAllResources(parentId);

    const resourceType = ResourceType.PROCESS_ARTIFACT;

    return (
        <QueryBaseComponent name="ProcessArtifacts" query={parametersQuery}>
            <SingleParentedOverviewComponent
                label={"Process Artifacts"}
                resourceQuery={parametersQuery}
                content={(id, displayedInOverview) => (
                    <ProcessArtifactOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: ProcessArtifactPropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
